import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import { ACTION_WEBSOCKET_NEW_MESSAGE } from '@/app/definitions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'

const ERROR_MESSAGE = 'Cannot start a chatbot test session. Please try again later.'

/**
 * Hook to prepare a single test contact for a Chatbot campaign test session.
 * - Fetches exactly 1 contact of the org which has no active subscription
 * (we can use subscribed one but that will mess up when multiple users are testing)
 * - When starting a test, triggers websocket newChat action
 */
export const useTestChatbot = (campaignId?: number | string) => {
  const dispatch = useDispatch()
  const orgId = useSelector(selectOrgId)
  const navigate = useNavigate()

  const numCampaignId = useMemo(() => (campaignId ? Number(campaignId) : undefined), [campaignId])

  const [listContacts, { data, isLoading: loading }] = esApi.endpoints.listContacts.useLazyQuery()

  const startTest = useCallback(async () => {
    if (!numCampaignId) return
    try {
      // we call it here to avoid fetching the same contact for multiple users
      // This is still cached by RTK Query if the same query is made again
      // so each user will use the same contact until the cache expires (or invalidated)
      const res = await listContacts({
        orgId: orgId.toString(),
        page: 1,
        size: 1,
        subscriptionfilter: 'isnot',
        subscriptionstatus: numCampaignId,
      }).unwrap()

      if (!res?.contacts?.length) {
        dispatch(
          createNotification({
            'empty-contacts': {
              type: 'warning',
              message: 'No available contacts to start a test session. {{link}}',
              links: [
                {
                  name: '{{link}}',
                  title: 'Please add a contact first.',
                  onClick: () => {
                    navigate('/contacts/add')
                    dispatch(createNotification({}))
                  },
                },
              ],
            },
          }),
        )
        return
      }

      const contact = res.contacts[0]

      dispatch({
        type: ACTION_WEBSOCKET_NEW_MESSAGE,
        payload: {
          action: 'newChat',
          campId: numCampaignId,
          contactId: contact.id,
        },
      })
    } catch (error) {
      dispatch(
        createNotification({
          'list-contacts': {
            type: 'error',
            message: getErrorMessage(error, ERROR_MESSAGE),
          },
        }),
      )
    }
  }, [numCampaignId, orgId, dispatch, listContacts, navigate])

  const contact = data?.contacts?.[0]

  return {
    contact,
    loading,
    startTest,
  }
}

export type UseTestChatbotReturn = ReturnType<typeof useTestChatbot>
